import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { OutstandingDisplay } from 'components/common';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { receiptWorksheetService, partyService, entityAccountingConfigService } from 'api/services';
import type {
  ReceiptAssignmentDto, AllocationLineRequest,
} from 'api/types';
import { assignmentStatusBadge } from './ReceiptFormModal';

// ============================================================================
// RECEIPT ALLOCATION MODAL (ADR-0024 Slice ① Leg 2 — the worksheet core).
// Customer AGAINST_REF picker grid + Advance / On Account / Adjustment
// remainder handling + Save Draft / Post / Return.
//
// The AGAINST_REF picker uses the worksheet's own outstanding-items endpoint
// (GET /receipt-assignments/outstanding-items?partyId=...) — the backend
// resolves the customer's AR ledger via accounts.tbl_party_ledger; an empty
// result simply means no open bills (use Advance / On Account instead).
// ============================================================================

const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const round2 = (n: number) => Math.round(n * 100) / 100;
const EPS = 0.005;

const ADJUSTMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'WHT_RECEIVABLE', label: 'WHT / TDS deducted' },
  { value: 'WRITE_OFF', label: 'Write off' },
  { value: 'ROUNDING', label: 'Rounding' },
];

const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const cellInput: React.CSSProperties = { width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' };
const sectionTitle: React.CSSProperties = { fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '1rem 0 0.375rem' };

interface OutItemRow {
  openItemId: string;
  referenceNo: string;
  itemDate?: string | null;
  dueDate?: string | null;
  /** Outstanding balance in the item currency. For a draft line whose open item
   *  no longer appears in the outstanding list (settled since), the draft amount
   *  is carried and the row is flagged `missing`. */
  fcBalance: number;
  /** The invoice's currency and its outstanding at the booked rate (booked rate = baseBalance / fcBalance). */
  currencyCode: string;
  baseBalance: number;
  /** Money applied, in the RECEIPT's currency. */
  allocate: string;
  /** Invoice in another currency than the receipt (ADR-0031 receipt revision): the invoice-currency amount discharged. */
  settle: string;
  /**
   * "Short-paid" (ADR-0060) — in the INVOICE's currency: what the customer deducted. The invoice
   * closes for it and it stays open as a short-paid item. Not money: it does not count toward the
   * allocation's balance. Editing it moves Allocate the other way, so the invoice stays closed.
   */
  short: string;
  reason: string;
  shortRemarks: string;
  /** When the item itself is a short-paid amount left by an earlier receipt — what it is. */
  deductionNote?: string;
  missing?: boolean;
}

interface PartyAllocationBucket {
  partyId: string;
  partyLabel: string;
  loading: boolean;
  items: OutItemRow[];
}

interface ExtraLine {
  key: number;
  mode: 'ADVANCE' | 'ON_ACCOUNT';
  partyId: string;
  partyLabel: string;
  referenceNo: string;
  amount: string;
  remarks: string;
}

interface AdjustmentLine {
  key: number;
  purposeCode: string;
  partyId: string;
  partyLabel: string;
  amount: string;
  referenceNo: string;
  remarks: string;
}

// ============================================================================
// RETURN REASON DIALOG — small prompt used by the modal + the screen row action.
// ============================================================================

export interface ReturnReasonDialogProps {
  isOpen: boolean;
  assignment: ReceiptAssignmentDto | null;
  onReturned: () => void;
  onClose: () => void;
}

export const ReturnReasonDialog: React.FC<ReturnReasonDialogProps> = ({ isOpen, assignment, onReturned, onClose }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (isOpen) { setReason(''); setSaving(false); } }, [isOpen]);

  const handleReturn = useCallback(async () => {
    if (!assignment) return;
    if (!reason.trim()) { notificationService.toast.warning('Enter the reason for returning this item.'); return; }
    setSaving(true);
    try {
      await receiptWorksheetService.return(assignment.id, { rowVersion: assignment.rowVersion, reason: reason.trim() });
      notificationService.toast.success(`Receipt portion ${assignment.receiptNo || ''} returned to the capturer.`);
      onReturned();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not return the worksheet item.');
    } finally {
      setSaving(false);
    }
  }, [assignment, reason, onReturned, onClose]);

  if (!isOpen || !assignment) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Return to Capturer — {assignment.receiptNo || ''}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Returning sends this portion of <strong>{money(assignment.fcAmount)} {assignment.currencyCode || ''}</strong> back
            to the capturer (wrong BU / unknown customer). It can then be redistributed from the Receipts register.
          </div>
          <label style={labelStyle}>Reason <span className="text-danger">*</span></label>
          <textarea className="form-textarea" rows={3} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Not our customer — belongs to the Gaborone BU"
            style={{ width: '100%', fontSize: '0.8125rem' }} />
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-danger" onClick={handleReturn} disabled={saving || !reason.trim()}>
            {saving ? 'Returning…' : 'Return'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// ALLOCATION MODAL
// ============================================================================

export interface ReceiptAllocationModalProps {
  isOpen: boolean;
  assignment: ReceiptAssignmentDto | null;
  /** Whether the user may post (UI.RECEIPT-WORKSHEET.APPROVE). */
  canPost: boolean;
  /** Whether the user may save a draft (UI.RECEIPT-WORKSHEET.UPDATE). */
  canSaveDraft: boolean;
  /** Whether the user may return the item (UI.RECEIPT-WORKSHEET.REJECT). */
  canReturn: boolean;
  onChanged: () => void;
  onClose: () => void;
}

export const ReceiptAllocationModal: React.FC<ReceiptAllocationModalProps> = ({
  isOpen, assignment, canPost, canSaveDraft, canReturn, onChanged, onClose,
}) => {
  const [detail, setDetail] = useState<ReceiptAssignmentDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<PartyAllocationBucket[]>([]);
  const [extraLines, setExtraLines] = useState<ExtraLine[]>([]);
  const [adjustments, setAdjustments] = useState<AdjustmentLine[]>([]);
  /** True once loading finished with NO pre-selected customer — remounts the
   *  headline Customer select with autoFocus (menu stays closed until typing). */
  const [focusCustomer, setFocusCustomer] = useState(false);
  /** The Leg-2 voucher posts on this date (resolves the FY). Defaults to the
   *  receipt date; the backend rejects anything earlier. */
  const [allocationDate, setAllocationDate] = useState('');
  /** Party picked in the headline select while the primary group has typed
   *  allocations — held until the user confirms discarding those inputs. */
  const [pendingCustomerChange, setPendingCustomerChange] = useState<string | null>(null);
  const [splitPickerOpen, setSplitPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postConfirmOpen, setPostConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reasonOptions, setReasonOptions] = useState<SelectOption[]>([]);
  useEffect(() => {
    if (!isOpen || reasonOptions.length) return;
    receiptWorksheetService.getDeductionReasons()
      .then(rs => setReasonOptions(rs.map(r => ({ value: r.value, label: r.label }))))
      .catch(() => setReasonOptions([]));
  }, [isOpen, reasonOptions.length]);

  const lineKey = useRef(0);
  const partyLabelCache = useRef<Map<string, string>>(new Map());
  // Home (functional) currency — for showing each invoice's booked value and the FX preview.
  const [homeCurrency, setHomeCurrency] = useState('');
  useEffect(() => {
    if (!isOpen || homeCurrency) return;
    entityAccountingConfigService.getConfig().then(c => setHomeCurrency(c.functionalCurrencyCode || '')).catch(() => {});
  }, [isOpen, homeCurrency]);

  const isPending = (detail ?? assignment)?.assignmentStatus === 'PENDING';
  const readOnly = !isPending;

  const markDirty = useCallback(() => setDirty(true), []);

  // ---- Outstanding items per customer (worksheet-scoped endpoint) ----
  // The assignment id restricts the bills to this worksheet item's branch.

  const loadItemsForParty = useCallback(async (partyId: string): Promise<OutItemRow[]> => {
    const items = await receiptWorksheetService.getOutstandingItems(partyId, assignment?.id);
    return items.map(it => ({
      openItemId: String(it.id),
      referenceNo: it.referenceNo,
      itemDate: it.itemDate,
      dueDate: it.dueDate,
      fcBalance: it.fcBalance,
      currencyCode: it.currencyCode,
      baseBalance: it.baseBalance,
      allocate: '',
      settle: '',
      short: '',
      reason: '',
      shortRemarks: '',
      deductionNote: it.deductionKind
        ? ['Short-paid', it.sourceReferenceNo ? `on ${it.sourceReferenceNo}` : '', it.reasonLabel || it.reasonCode || '', it.remarks || ''].filter(Boolean).join(' · ')
        : undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment?.id]);

  // ---- Open: load the item + hydrate any existing draft lines ----

  useEffect(() => {
    if (!isOpen || !assignment?.id) {
      setDetail(null); setGroups([]); setExtraLines([]); setAdjustments([]); setDirty(false);
      setFocusCustomer(false); setPendingCustomerChange(null); setSplitPickerOpen(false);
      setAllocationDate('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setGroups([]); setExtraLines([]); setAdjustments([]); setDirty(false);
    setFocusCustomer(false); setPendingCustomerChange(null); setSplitPickerOpen(false);
    setAllocationDate('');
    (async () => {
      try {
        const d = await receiptWorksheetService.getById(assignment.id);
        if (cancelled) return;
        setDetail(d);
        // Allocation date defaults to the receipt date (the earliest the backend allows).
        setAllocationDate((d.receiptDate || '').slice(0, 10));
        (d.lines || []).forEach(l => {
          if (l.partyId && l.partyName) partyLabelCache.current.set(l.partyId, l.partyName);
        });

        // Hydrate ADVANCE / ON_ACCOUNT / ADJUSTMENT draft lines.
        const extras: ExtraLine[] = (d.lines || [])
          .filter(l => l.allocationMode === 'ADVANCE' || l.allocationMode === 'ON_ACCOUNT')
          .map(l => ({
            key: ++lineKey.current,
            mode: l.allocationMode as 'ADVANCE' | 'ON_ACCOUNT',
            partyId: l.partyId || '',
            partyLabel: l.partyName || '',
            referenceNo: l.referenceNo || '',
            amount: l.fcAmount ? String(l.fcAmount) : '',
            remarks: l.remarks || '',
          }));
        const adjs: AdjustmentLine[] = (d.lines || [])
          .filter(l => l.allocationMode === 'ADJUSTMENT')
          .map(l => ({
            key: ++lineKey.current,
            purposeCode: l.adjustmentPurposeCode || 'WHT_RECEIVABLE',
            partyId: l.partyId || '',
            partyLabel: l.partyName || '',
            amount: l.fcAmount ? String(l.fcAmount) : '',
            referenceNo: l.referenceNo || '',
            remarks: l.remarks || '',
          }));
        setExtraLines(extras);
        setAdjustments(adjs);

        // Hydrate AGAINST_REF drafts: load each party's outstanding items, then
        // apply the drafted amounts (append a flagged row when the open item is
        // no longer outstanding).
        const refLines = (d.lines || []).filter(l => l.allocationMode === 'AGAINST_REF' && l.partyId);
        const partyIds = Array.from(new Set(refLines.map(l => l.partyId as string)));
        // Seed the default customer from the assignment hint when no draft exists.
        if (partyIds.length === 0 && d.partyId) partyIds.push(d.partyId);
        if (d.partyId && d.partyName) partyLabelCache.current.set(d.partyId, d.partyName);

        for (const pid of partyIds) {
          const label = partyLabelCache.current.get(pid) || '';
          setGroups(prev => [...prev, { partyId: pid, partyLabel: label, loading: true, items: [] }]);
          // eslint-disable-next-line no-await-in-loop
          const items = await loadItemsForParty(pid).catch(() => [] as OutItemRow[]);
          if (cancelled) return;
          const mine = refLines.filter(l => l.partyId === pid);
          const withDraft = items.map(it => {
            const drafted = mine.find(l => l.openItemId === it.openItemId);
            return drafted ? {
              ...it,
              allocate: drafted.fcAmount ? String(drafted.fcAmount) : '',
              settle: drafted.settledFcAmount != null ? String(drafted.settledFcAmount) : '',
              short: drafted.shortPaidFcAmount ? String(drafted.shortPaidFcAmount) : '',
              reason: drafted.shortPaidReasonCode || '',
              shortRemarks: drafted.shortPaidRemarks || '',
            } : it;
          });
          mine.filter(l => !items.some(it => it.openItemId === l.openItemId)).forEach(l => {
            withDraft.push({
              openItemId: l.openItemId || '',
              referenceNo: l.openItemReferenceNo || '(no longer outstanding)',
              itemDate: null, dueDate: null,
              fcBalance: l.fcAmount + (l.shortPaidFcAmount || 0),
              currencyCode: l.settledCurrency || d.currencyCode || '',
              baseBalance: 0,
              allocate: l.fcAmount ? String(l.fcAmount) : '',
              settle: l.settledFcAmount != null ? String(l.settledFcAmount) : '',
              short: l.shortPaidFcAmount ? String(l.shortPaidFcAmount) : '',
              reason: l.shortPaidReasonCode || '',
              shortRemarks: l.shortPaidRemarks || '',
              missing: true,
            });
          });
          setGroups(prev => prev.map(g => (g.partyId === pid ? { ...g, loading: false, items: withDraft } : g)));
        }
        // No pre-selected customer (no drafts, no hint party) → focus the picker.
        if (partyIds.length === 0) setFocusCustomer(true);
      } catch (err: any) {
        if (!cancelled) notificationService.toast.error(err?.response?.data?.message || 'Could not load the worksheet item.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assignment?.id]);

  // ---- Pickers ----

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined });
      r.data.forEach(p => partyLabelCache.current.set(p.id, `${p.partyCode} - ${p.partyName}`));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}` }));
    } catch {
      return [];
    }
  }, []);

  const addCustomer = useCallback(async (partyId: string) => {
    if (!partyId) return;
    if (groups.some(g => g.partyId === partyId)) {
      notificationService.toast.info('That customer is already on the worksheet.');
      return;
    }
    const label = partyLabelCache.current.get(partyId) || '';
    setGroups(prev => [...prev, { partyId, partyLabel: label, loading: true, items: [] }]);
    const items = await loadItemsForParty(partyId).catch(() => [] as OutItemRow[]);
    setGroups(prev => prev.map(g => (g.partyId === partyId ? { ...g, loading: false, items } : g)));
  }, [groups, loadItemsForParty]);

  /** Replace the PRIMARY customer group (the headline picker's target). */
  const applyPrimaryCustomer = useCallback(async (partyId: string) => {
    if (!partyId) return;
    if (groups[0]?.partyId === partyId) return;
    if (groups.some((g, i) => i > 0 && g.partyId === partyId)) {
      notificationService.toast.info('That customer is already on the worksheet.');
      return;
    }
    const label = partyLabelCache.current.get(partyId) || '';
    setGroups(prev => [{ partyId, partyLabel: label, loading: true, items: [] }, ...prev.slice(1)]);
    markDirty();
    const items = await loadItemsForParty(partyId).catch(() => [] as OutItemRow[]);
    setGroups(prev => prev.map(g => (g.partyId === partyId ? { ...g, loading: false, items } : g)));
  }, [groups, loadItemsForParty, markDirty]);

  /** Headline picker change — confirm before discarding typed allocations. */
  const handlePrimaryCustomerChange = useCallback((partyId: string) => {
    if (!partyId) return;
    const primary = groups[0];
    const hasTyped = !!primary && primary.items.some(it => num(it.allocate) > 0);
    if (hasTyped && partyId !== primary.partyId) {
      setPendingCustomerChange(partyId);
      return;
    }
    applyPrimaryCustomer(partyId);
  }, [groups, applyPrimaryCustomer]);

  const removeGroup = useCallback((partyId: string) => {
    setGroups(prev => prev.filter(g => g.partyId !== partyId));
    markDirty();
  }, [markDirty]);

  /** ADR-0060: a short-paid amount, in the invoice's currency, capped at its balance. */
  const rowShort = (it: OutItemRow) => Math.max(0, Math.min(num(it.short), it.fcBalance));
  /** A row without a short-paid amount loses its reason and remarks too. */
  const withShort = (it: OutItemRow, s: number): OutItemRow =>
    s > EPS ? { ...it, short: String(round2(s)) } : { ...it, short: '', reason: '', shortRemarks: '' };
  const patchRow = useCallback((partyId: string, openItemId: string, f: (it: OutItemRow) => OutItemRow) => {
    setGroups(prev => prev.map(g => (g.partyId === partyId
      ? { ...g, items: g.items.map(it => (it.openItemId === openItemId ? f(it) : it)) }
      : g)));
    markDirty();
  }, [markDirty]);

  // A short-paid invoice stays closed: editing what is received (or settled, across currencies)
  // moves the short-paid amount the other way.
  const updateAllocate = useCallback((partyId: string, openItemId: string, value: string) => {
    patchRow(partyId, openItemId, it => {
      const sameCcy = !it.currencyCode || !receiptCurrencyRef.current || it.currencyCode === receiptCurrencyRef.current;
      // Same currency: the amount received IS the amount settled, so it is capped at the invoice's
      // balance (as on the Receipt / Payment Voucher). The excess belongs to Advance / On Account,
      // never to FX. Across currencies the cap is meaningless — more money than the booked value is
      // exactly how a gain looks.
      const capped = sameCcy && !it.missing && value !== '' ? String(Math.min(num(value), it.fcBalance)) : value;
      const next = { ...it, allocate: capped };
      return rowShort(it) > 0 && sameCcy ? withShort(next, it.fcBalance - num(capped)) : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRow]);
  const updateSettle = useCallback((partyId: string, openItemId: string, value: string) => {
    patchRow(partyId, openItemId, it => {
      const next = { ...it, settle: value };
      return rowShort(it) > 0 ? withShort(next, it.fcBalance - num(value)) : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRow]);
  /**
   * Editing Short-paid moves Allocate the other way (ADR-0060): Allocate = Balance − Short-paid. On
   * an invoice in another currency, Settles takes the rest and Allocate follows at the booked rate.
   * Short-paid in full leaves nothing allocated — allowed.
   */
  const updateShort = useCallback((partyId: string, openItemId: string, value: string) => {
    patchRow(partyId, openItemId, it => {
      const s = value === '' ? 0 : Math.min(Math.max(0, num(value)), it.fcBalance);
      const rest = round2(it.fcBalance - s);
      const next = withShort(it, s);
      const cross = !!it.currencyCode && !!receiptCurrencyRef.current && it.currencyCode !== receiptCurrencyRef.current;
      if (cross) {
        const r0 = it.fcBalance > 0 ? it.baseBalance / it.fcBalance : 1;
        const rate = receiptRateRef.current > 0 ? receiptRateRef.current : 1;
        return { ...next, settle: rest > 0 ? String(rest) : '', allocate: rest > 0 ? String(round2((rest * r0) / rate)) : '' };
      }
      return { ...next, allocate: rest > 0 ? String(rest) : '' };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRow]);
  const updateShortField = useCallback((partyId: string, openItemId: string, patch: Partial<Pick<OutItemRow, 'reason' | 'shortRemarks'>>) => {
    patchRow(partyId, openItemId, it => ({ ...it, ...patch }));
  }, [patchRow]);

  // ---- FX (ADR-0031 receipt revision) — mirror of the server's settlement math ----
  // Each invoice clears at ITS booked rate (baseBalance / fcBalance); the money arrives at the
  // receipt's rate; the difference is realized FX (positive = gain). An invoice in another
  // currency than the receipt needs the invoice-currency figure (settle).
  const receiptCurrency = (detail ?? assignment)?.currencyCode || homeCurrency;
  const receiptRate = (detail ?? assignment)?.exchangeRate || 1;
  const receiptCurrencyRef = useRef(receiptCurrency); receiptCurrencyRef.current = receiptCurrency;
  const receiptRateRef = useRef(receiptRate); receiptRateRef.current = receiptRate;
  const isCross = useCallback((it: OutItemRow) => !!it.currencyCode && !!receiptCurrency && it.currencyCode !== receiptCurrency, [receiptCurrency]);
  const rowFx = useCallback((it: OutItemRow) => {
    const amt = num(it.allocate);
    if (amt <= 0 || it.missing || it.fcBalance <= 0) return { cleared: 0, cash: 0, fx: 0 };
    const r0 = it.baseBalance / it.fcBalance;
    const cross = isCross(it);
    const fcDelta = cross ? Math.min(num(it.settle), it.fcBalance) : Math.min(amt, it.fcBalance);
    if (fcDelta <= 0) return { cleared: 0, cash: round2(amt * receiptRate), fx: 0 };
    const cleared = Math.abs(fcDelta - it.fcBalance) < EPS ? it.baseBalance : round2(fcDelta * r0);
    // Mirror of ReceivableSettlement (and the Receipt Voucher form): on a same-currency invoice only
    // the part that SETTLES is valued — an amount above the balance is an over-allocation, never FX.
    // Across currencies the whole money settles the stated figure, so it is all valued.
    const cash = cross ? round2(amt * receiptRate) : round2(fcDelta * receiptRate);
    return { cleared, cash, fx: round2(cash - cleared) };
  }, [isCross, receiptRate]);

  // ---- Other lines (Advance / On Account / Adjustments) ----
  // Created from the Difference row while unapplied ≠ 0; party prefilled from
  // the selected (primary) customer, editable afterwards.

  const primaryParty = useCallback((): { partyId: string; partyLabel: string } => {
    const pid = groups[0]?.partyId || detail?.partyId || '';
    const label = pid
      ? (partyLabelCache.current.get(pid) || groups[0]?.partyLabel || detail?.partyName || '')
      : '';
    return { partyId: pid, partyLabel: label };
  }, [groups, detail]);

  const addExtraLine = useCallback((mode: 'ADVANCE' | 'ON_ACCOUNT', prefillAmount: number) => {
    const p = primaryParty();
    setExtraLines(prev => [...prev, {
      key: ++lineKey.current, mode,
      partyId: p.partyId, partyLabel: p.partyLabel,
      referenceNo: '', amount: prefillAmount > EPS ? String(round2(prefillAmount)) : '', remarks: '',
    }]);
    markDirty();
  }, [primaryParty, markDirty]);
  const updateExtraLine = useCallback((key: number, patch: Partial<ExtraLine>) => {
    setExtraLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));
    markDirty();
  }, [markDirty]);
  const removeExtraLine = useCallback((key: number) => {
    setExtraLines(prev => prev.filter(l => l.key !== key));
    markDirty();
  }, [markDirty]);

  const addAdjustment = useCallback((purposeCode: string, prefillAmount: number) => {
    const p = primaryParty();
    setAdjustments(prev => [...prev, {
      key: ++lineKey.current, purposeCode,
      // Party prefilled from the selected customer; optional for ROUNDING.
      partyId: p.partyId, partyLabel: p.partyLabel,
      amount: Math.abs(prefillAmount) > EPS ? String(round2(prefillAmount)) : '',
      referenceNo: '', remarks: '',
    }]);
    markDirty();
  }, [primaryParty, markDirty]);
  const updateAdjustment = useCallback((key: number, patch: Partial<AdjustmentLine>) => {
    setAdjustments(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));
    markDirty();
  }, [markDirty]);
  const removeAdjustment = useCallback((key: number) => {
    setAdjustments(prev => prev.filter(l => l.key !== key));
    markDirty();
  }, [markDirty]);

  // ---- Totals ----

  const portionAmount = detail?.fcAmount ?? assignment?.fcAmount ?? 0;
  const againstRefTotal = useMemo(
    () => groups.reduce((s, g) => s + g.items.reduce((x, it) => x + num(it.allocate), 0), 0),
    [groups],
  );
  const extraTotal = useMemo(() => extraLines.reduce((s, l) => s + num(l.amount), 0), [extraLines]);
  const customersTotal = againstRefTotal + extraTotal;
  const adjustmentNet = useMemo(() => adjustments.reduce((s, l) => s + num(l.amount), 0), [adjustments]);
  const available = portionAmount + adjustmentNet;
  const unapplied = available - customersTotal;
  const balanced = Math.abs(unapplied) < EPS && customersTotal > 0;
  const fxTotal = useMemo(
    () => round2(groups.reduce((s, g) => s + g.items.reduce((x, it) => x + rowFx(it).fx, 0), 0)),
    [groups, rowFx],
  );

  // "Auto-allocate oldest first" — spread the current unapplied amount across
  // the loaded open items in item-date order (topping up partial allocations).
  const autoAllocate = useCallback(() => {
    let remaining = round2(unapplied);
    if (remaining <= EPS) { notificationService.toast.info('Nothing left to allocate.'); return; }
    const ordered = groups
      // Same-currency invoices only: one in another currency needs two figures the user states.
      .flatMap(g => g.items.filter(it => !it.missing && !isCross(it)).map(it => ({ partyId: g.partyId, it })))
      .sort((a, b) => (a.it.itemDate || '').localeCompare(b.it.itemDate || ''));
    if (ordered.length === 0) { notificationService.toast.info('No outstanding bills loaded — pick a customer first.'); return; }
    setGroups(prev => {
      const next = prev.map(g => ({ ...g, items: g.items.map(it => ({ ...it })) }));
      for (const { partyId, it } of ordered) {
        if (remaining <= EPS) break;
        const grp = next.find(g => g.partyId === partyId);
        const row = grp?.items.find(x => x.openItemId === it.openItemId);
        if (!row) continue;
        const headroom = round2(row.fcBalance - num(row.allocate));
        if (headroom <= EPS) continue;
        const take = Math.min(headroom, remaining);
        row.allocate = String(round2(num(row.allocate) + take));
        remaining = round2(remaining - take);
      }
      return next;
    });
    markDirty();
  }, [groups, unapplied, markDirty, isCross]);

  /**
   * "Short-paid, keep open" (ADR-0060): the invoices were allocated in full but less money arrived
   * (unapplied below zero). Take the excess off the newest allocated invoices and keep it open as
   * their short-paid amount — the allocation then balances and every invoice still closes.
   * Same-currency invoices only (one in another currency needs its figures stated by hand).
   */
  const shortPayExcess = useCallback(() => {
    let excess = round2(-unapplied);
    if (excess <= EPS) return;
    const ordered = groups
      .flatMap(g => g.items.filter(it => !it.missing && !isCross(it) && num(it.allocate) > 0).map(it => ({ partyId: g.partyId, it })))
      .sort((a, b) => (b.it.itemDate || '').localeCompare(a.it.itemDate || ''));
    if (ordered.length === 0) { notificationService.toast.info('Allocate the invoices first — the shortfall is taken from the newest one.'); return; }
    setGroups(prev => {
      const next = prev.map(g => ({ ...g, items: g.items.map(it => ({ ...it })) }));
      for (const { partyId, it } of ordered) {
        if (excess <= EPS) break;
        const row = next.find(g => g.partyId === partyId)?.items.find(x => x.openItemId === it.openItemId);
        if (!row) continue;
        const take = Math.min(num(row.allocate), excess);
        const left = round2(num(row.allocate) - take);
        row.allocate = left > EPS ? String(left) : '';
        row.short = String(round2(rowShort(row) + take));
        excess = round2(excess - take);
      }
      return next;
    });
    markDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, unapplied, markDirty, isCross]);

  /** Short-paid amounts kept open, in base at each invoice's booked rate (the summary line). */
  const shortSummary = useMemo(() => {
    let base = 0; let count = 0;
    groups.forEach(g => g.items.forEach(it => {
      const s = rowShort(it);
      if (s <= 0) return;
      count++;
      base += it.fcBalance > 0 && it.baseBalance > 0 ? s * (it.baseBalance / it.fcBalance) : s;
    }));
    return { base: round2(base), count };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // ---- Build + validate lines ----

  const buildLines = useCallback((): AllocationLineRequest[] => {
    const lines: AllocationLineRequest[] = [];
    groups.forEach(g => g.items.forEach(it => {
      const amt = num(it.allocate);
      const s = rowShort(it);
      // A short-paid invoice posts even when nothing was received for it (ADR-0060).
      if (amt > 0 || s > 0) {
        lines.push({
          allocationMode: 'AGAINST_REF', partyId: g.partyId, openItemId: it.openItemId, amount: round2(amt),
          // Another currency than the receipt: the invoice-currency amount discharged (two-figure model).
          settledFcAmount: amt > 0 && isCross(it) && num(it.settle) > 0 ? round2(num(it.settle)) : undefined,
          shortPaidAmount: s > 0 ? round2(s) : undefined,
          shortPaidReasonCode: s > 0 ? (it.reason || undefined) : undefined,
          shortPaidRemarks: s > 0 ? (it.shortRemarks.trim() || undefined) : undefined,
        });
      }
    }));
    extraLines.forEach(l => {
      const amt = num(l.amount);
      if (amt > 0 || l.partyId) {
        lines.push({
          allocationMode: l.mode, partyId: l.partyId || undefined,
          amount: round2(amt), referenceNo: l.referenceNo.trim() || undefined,
          remarks: l.remarks.trim() || undefined,
        });
      }
    });
    adjustments.forEach(l => {
      const amt = num(l.amount);
      if (amt !== 0 || l.purposeCode) {
        lines.push({
          allocationMode: 'ADJUSTMENT', adjustmentPurposeCode: l.purposeCode || undefined,
          partyId: l.partyId || undefined, amount: round2(amt),
          referenceNo: l.referenceNo.trim() || undefined, remarks: l.remarks.trim() || undefined,
        });
      }
    });
    return lines;
  }, [groups, extraLines, adjustments, isCross]);

  const validate = useCallback((forPost: boolean): string | null => {
    for (const g of groups) {
      for (const it of g.items) {
        const amt = num(it.allocate);
        const s = rowShort(it);
        if (amt < 0 || num(it.short) < 0) return 'Allocation amounts cannot be negative.';
        if (s > 0 && !it.reason) return `${it.referenceNo} is short-paid — pick a reason.`;
        if (it.missing) continue;
        if (isCross(it)) {
          if (amt > 0 && !(num(it.settle) > 0))
            return `${it.referenceNo} is in ${it.currencyCode} and the receipt in ${receiptCurrency} — enter how much ${it.currencyCode} this ${money(amt)} ${receiptCurrency} settles.`;
          if (num(it.settle) + s - it.fcBalance > EPS)
            return `Settling ${it.currencyCode} ${money(num(it.settle))}${s > 0 ? ` and short-paid ${money(s)}` : ''} on ${it.referenceNo} exceeds its balance ${money(it.fcBalance)}.`;
        } else if (amt + s - it.fcBalance > EPS) {
          return `Allocation${s > 0 ? ' and short-paid together' : ''} for ${it.referenceNo} exceed${s > 0 ? '' : 's'} its balance ${money(it.fcBalance)}.`;
        }
      }
    }
    for (const l of extraLines) {
      if (num(l.amount) > 0 && !l.partyId) return `${l.mode === 'ADVANCE' ? 'Advance' : 'On Account'} lines need a customer.`;
      if (l.partyId && num(l.amount) <= 0) return `${l.mode === 'ADVANCE' ? 'Advance' : 'On Account'} lines need a positive amount.`;
    }
    for (const l of adjustments) {
      if (!l.purposeCode) return 'Pick the adjustment type.';
      if (num(l.amount) === 0) return 'Adjustment amounts cannot be zero.';
    }
    if (forPost) {
      const lines = buildLines();
      if (lines.length === 0) return 'Add at least one allocation line.';
      if (!balanced) return `The allocation is not balanced — unapplied ${money(unapplied)}. Allocate it, add an adjustment, or route it to Advance / On Account.`;
      if (!allocationDate) return 'Enter the allocation date.';
      const minDate = ((detail ?? assignment)?.receiptDate || '').slice(0, 10);
      if (minDate && allocationDate < minDate) {
        return `The allocation date cannot be before the receipt date (${fmtDate(minDate)}).`;
      }
    }
    return null;
  }, [groups, extraLines, adjustments, buildLines, balanced, unapplied, allocationDate, detail, assignment, isCross, receiptCurrency]);

  // ---- Actions ----

  const handleSaveDraft = useCallback(async () => {
    if (!detail) return;
    const err = validate(false);
    if (err) { notificationService.toast.warning(err); return; }
    setSaving(true);
    try {
      const updated = await receiptWorksheetService.saveAllocation(detail.id, { rowVersion: detail.rowVersion, lines: buildLines() });
      setDetail(updated);
      setDirty(false);
      notificationService.toast.success('Allocation draft saved.');
      onChanged();
    } catch (err2: any) {
      notificationService.toast.error(err2?.response?.data?.message || 'Could not save the allocation draft.');
    } finally {
      setSaving(false);
    }
  }, [detail, validate, buildLines, onChanged]);

  const handlePostClick = useCallback(() => {
    const err = validate(true);
    if (err) { notificationService.toast.warning(err); return; }
    setPostConfirmOpen(true);
  }, [validate]);

  const handleConfirmPost = useCallback(async () => {
    if (!detail) return;
    setPostConfirmOpen(false);
    setPosting(true);
    try {
      const updated = await receiptWorksheetService.postAllocation(detail.id, {
        rowVersion: detail.rowVersion,
        allocationDate,
        lines: buildLines(),
      });
      notificationService.toast.success(`Receipt allocation posted${updated.allocationTransactionNo ? ` — voucher ${updated.allocationTransactionNo}` : ''}.`);
      setDirty(false);
      onChanged();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not post the allocation.');
    } finally {
      setPosting(false);
    }
  }, [detail, allocationDate, buildLines, onChanged, onClose]);

  const handleClose = useCallback(() => { if (dirty && isPending) setDiscardConfirmOpen(true); else onClose(); }, [dirty, isPending, onClose]);

  if (!isOpen || !assignment) return null;
  const d = detail ?? assignment;
  const busy = saving || posting;

  // Identification helpers — panel shows only non-empty items.
  const instrumentText = [
    [d.instrumentType, d.instrumentNo].filter(Boolean).join(' '),
    d.instrumentDate ? fmtDate(d.instrumentDate) : '',
    d.drawerBank || '',
  ].filter(Boolean).join(' · ');
  const showReceiptTotal = (d.receiptFcAmount ?? 0) > EPS && Math.abs((d.receiptFcAmount ?? 0) - portionAmount) > EPS;
  const idLabel: React.CSSProperties = { color: 'var(--text-muted)' };
  const smallBtn: React.CSSProperties = { fontSize: '0.75rem', padding: '0.25rem 0.6rem' };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {(busy || loading) && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {posting ? 'Posting…' : saving ? 'Saving…' : 'Loading…'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Allocate Receipt — {d.receiptNo || '—'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={busy}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {/* Identification panel — recognise the payment + customer before allocating */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.8125rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.6rem 0.75rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span><span style={idLabel}>Receipt No </span><strong>{d.receiptNo || '—'}</strong></span>
              <span><span style={idLabel}>Date of transfer </span><strong>{fmtDate(d.receiptDate)}</strong></span>
              <span><span style={idLabel}>Bank </span><strong>{d.bankLedgerName || '—'}</strong></span>
              <span>
                <span style={idLabel}>This portion </span>
                <strong>{money(portionAmount)} {d.currencyCode || ''}</strong>
                {showReceiptTotal && <span style={idLabel}> of receipt total {money(d.receiptFcAmount)}</span>}
              </span>
              {assignmentStatusBadge(d.assignmentStatus)}
            </div>
            {(d.partyHintText || d.payerText || d.receiptReference || instrumentText || d.remarks || d.receiptNarration || d.icInTransactionId) && (
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                {d.partyHintText && (
                  <span className="badge" style={{ background: '#d97706', color: '#fff' }} title="The capturer's hint for this portion">
                    Hint: {d.partyHintText}
                  </span>
                )}
                {d.icInTransactionId && (
                  <span className="badge" style={{ background: '#0891b2', color: '#fff' }}
                    title="This portion was routed from another group entity via an intercompany transfer — the bank shown is the receiving entity's source bank">
                    ⇄ via intercompany
                  </span>
                )}
                {d.payerText && <span><span style={idLabel}>Bank remarks </span><strong>{d.payerText}</strong></span>}
                {d.receiptReference && <span><span style={idLabel}>Reference </span><strong>{d.receiptReference}</strong></span>}
                {instrumentText && <span><span style={idLabel}>Instrument </span><strong>{instrumentText}</strong></span>}
                {d.remarks && <span><span style={idLabel}>Remarks </span><strong>{d.remarks}</strong></span>}
                {d.receiptNarration && <span><span style={idLabel}>Narration </span><strong>{d.receiptNarration}</strong></span>}
              </div>
            )}
          </div>

          {/* Customer — the headline. Pre-selected from the hint party; replaces
              the primary group on change (confirm when typed allocations exist).
              Allocation Date: the Leg-2 voucher posts on this date (min = receipt date). */}
          <div className="form-grid-3" style={{ alignItems: 'flex-end' }}>
            <div>
              <SyutSelect key={focusCustomer ? 'customer-focus' : 'customer'}
                label="Customer" isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={groups[0]?.partyId
                  ? [{ value: groups[0].partyId, label: groups[0].partyLabel || partyLabelCache.current.get(groups[0].partyId) || '' }]
                  : undefined}
                menuPortalTarget={document.body}
                value={groups[0]?.partyId || ''}
                isDisabled={readOnly}
                autoFocus={focusCustomer && !readOnly}
                onChange={(v) => handlePrimaryCustomerChange((v as string) || '')}
                placeholder="Pick the customer this money belongs to"
                isClearable={false} size="sm" />
              {/* Their position, on the same terms as the bills below. The unit is the
                  ASSIGNMENT's branch, not the session's: the picker offers only that branch's
                  bills, so a session-unit figure here would describe something else (ADR-0042 §6).
                  Only for the single-customer case — with 2+ groups each header carries its own. */}
              {groups.length === 1 && (
                <div style={{ marginTop: '0.3rem' }}>
                  <OutstandingDisplay partyId={groups[0]?.partyId} businessUnitId={d.businessUnitId}
                    side="receivable" variant="inline" />
                </div>
              )}
            </div>
            {!readOnly && (
              <div>
                <label style={labelStyle}>Allocation Date <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={allocationDate}
                  min={(d.receiptDate || '').slice(0, 10) || undefined}
                  onChange={(e) => setAllocationDate(e.target.value)}
                  title="The allocation voucher posts on this date — cannot be before the receipt date"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            )}
            {!readOnly && (
              <div style={{ paddingBottom: '0.125rem' }}>
                <button className="btn btn-secondary" onClick={autoAllocate}
                  disabled={busy || groups.every(g => g.items.filter(it => !it.missing).length === 0)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                  <IconSystem.check size={14} /> Auto-allocate oldest first
                </button>
              </div>
            )}
          </div>

          {/* Outstanding items — one bills table per customer. With a single
              customer no group header renders; 2+ customers get headers + ✕. */}
          {groups.map(g => {
            const groupTotal = g.items.reduce((s, it) => s + num(it.allocate), 0);
            const showHeader = groups.length > 1;
            return (
              <div key={g.partyId} style={{ marginTop: '0.875rem' }}>
                {showHeader && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {g.partyLabel || 'Customer'}
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                        {' '}— allocated {money(groupTotal)}
                      </span>
                      {/* What they owe in this assignment's branch, beside what we allocate */}
                      <span style={{ fontWeight: 400, marginLeft: '0.5rem' }}>
                        <OutstandingDisplay partyId={g.partyId} businessUnitId={d.businessUnitId}
                          side="receivable" variant="inline" />
                      </span>
                    </span>
                    {!readOnly && (
                      <button title="Remove customer" onClick={() => removeGroup(g.partyId)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.2rem' }}>
                        <IconSystem.x size={14} />
                      </button>
                    )}
                  </div>
                )}
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                      <th style={{ textAlign: 'left', padding: '0.45rem 0.5rem' }}>Ref No</th>
                      <th style={{ width: 105, textAlign: 'left', padding: '0.45rem 0.5rem' }}>Date</th>
                      <th style={{ width: 105, textAlign: 'left', padding: '0.45rem 0.5rem' }}>Due Date</th>
                      <th style={{ width: 150, textAlign: 'right', padding: '0.45rem 0.5rem' }}>Balance</th>
                      <th style={{ width: 170, textAlign: 'right', padding: '0.45rem 0.5rem' }}>Allocate ({receiptCurrency})</th>
                      <th style={{ width: 120, textAlign: 'right', padding: '0.45rem 0.5rem' }}
                        title="What the customer deducted from this invoice. It closes for it, and it stays open on their ledger as a short-paid item. Not money — it does not count toward the allocation.">
                        Short-paid
                      </th>
                      <th style={{ width: 150, textAlign: 'left', padding: '0.45rem 0.5rem' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.loading && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '0.75rem', color: 'var(--text-muted)' }}>Loading outstanding bills…</td></tr>
                    )}
                    {!g.loading && g.items.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '0.75rem', color: 'var(--text-muted)' }}>
                          No open bills for this customer in this branch — use Advance / On Account instead.
                        </td>
                      </tr>
                    )}
                    {!g.loading && g.items.map(it => (
                      <React.Fragment key={it.openItemId}>
                      <tr style={{ borderBottom: rowShort(it) > 0 ? 'none' : '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <strong>{it.referenceNo}</strong>
                          {it.missing && <span style={{ color: '#d97706', marginLeft: '0.375rem', fontSize: '0.72rem' }}>drafted item no longer outstanding</span>}
                          {it.deductionNote && <div style={{ fontSize: '0.7rem', color: 'var(--warning-text, #92400e)' }}>{it.deductionNote}</div>}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{fmtDate(it.itemDate)}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{fmtDate(it.dueDate)}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>
                          {/* One-click fill. Same currency: allocate min(balance, unapplied incl. this row's
                              own amount). Another currency: settle the whole balance and allocate its
                              booked equivalent in the receipt's currency (FX 0), capped at unapplied. */}
                          {(() => {
                            const fillRow = () => {
                              const own = Math.max(0, round2(unapplied + num(it.allocate)));
                              if (isCross(it)) {
                                const bookedEquivalent = receiptRate > 0 ? round2(it.baseBalance / receiptRate) : 0;
                                updateSettle(g.partyId, it.openItemId, String(it.fcBalance));
                                const fill = Math.min(bookedEquivalent, own);
                                updateAllocate(g.partyId, it.openItemId, fill > EPS ? String(fill) : '');
                              } else {
                                const fill = Math.min(it.fcBalance, own);
                                updateAllocate(g.partyId, it.openItemId, fill > EPS ? String(round2(fill)) : '');
                              }
                            };
                            const text = `${it.currencyCode && it.currencyCode !== receiptCurrency ? `${it.currencyCode} ` : ''}${money(it.fcBalance)}`;
                            return !readOnly && !it.missing ? (
                              <span title="Click to allocate" role="button" tabIndex={0}
                                onClick={fillRow}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fillRow(); } }}
                                style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}>
                                {text}
                              </span>
                            ) : <>{text}</>;
                          })()}
                          {!it.missing && homeCurrency && it.currencyCode && it.currencyCode !== homeCurrency && it.fcBalance > 0 && (
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{homeCurrency} {money(it.baseBalance)} @ {(it.baseBalance / it.fcBalance).toFixed(4)} booked</div>
                          )}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <input type="number" inputMode="decimal" className="form-input" value={it.allocate}
                            disabled={readOnly}
                            onChange={(e) => updateAllocate(g.partyId, it.openItemId, e.target.value)}
                            placeholder="0.00" style={{ ...cellInput, textAlign: 'right' }} />
                          {isCross(it) && !it.missing && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.2rem' }}>
                              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>settles {it.currencyCode}</span>
                              <input type="number" inputMode="decimal" className="form-input" value={it.settle}
                                disabled={readOnly} title={`How much ${it.currencyCode} this money discharges`}
                                onChange={(e) => updateSettle(g.partyId, it.openItemId, e.target.value)}
                                placeholder="0.00" style={{ ...cellInput, textAlign: 'right' }} />
                            </div>
                          )}
                          {(() => {
                            const f = rowFx(it);
                            return f.fx !== 0 ? (
                              <div style={{ fontSize: '0.68rem', textAlign: 'right', color: f.fx > 0 ? '#16a34a' : '#dc2626' }}
                                title={`Invoice clears ${homeCurrency} ${money(f.cleared)} at its booked rate; the money is worth ${homeCurrency} ${money(f.cash)} at the receipt's rate`}>
                                FX {f.fx > 0 ? 'gain' : 'loss'} {homeCurrency} {money(Math.abs(f.fx))}
                              </div>
                            ) : null;
                          })()}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <input type="number" inputMode="decimal" className="form-input" value={it.short}
                            disabled={readOnly || it.missing} aria-label={`Short-paid on ${it.referenceNo}`}
                            onChange={(e) => updateShort(g.partyId, it.openItemId, e.target.value)}
                            placeholder="—" style={{ ...cellInput, textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          {rowShort(it) > 0 ? (
                            <SyutSelect options={reasonOptions} value={it.reason} isDisabled={readOnly}
                              onChange={(v) => updateShortField(g.partyId, it.openItemId, { reason: (v as string) || '' })}
                              menuPortalTarget={document.body} placeholder="Pick a reason" isClearable={false} size="sm" />
                          ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                      </tr>
                      {rowShort(it) > 0 && (
                        <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--warning-bg, #fef3c7)' }}>
                          <td colSpan={7} style={{ padding: '0.25rem 0.5rem 0.4rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Short-paid remarks</span>
                              <input type="text" className="form-input" maxLength={500} disabled={readOnly}
                                style={{ fontSize: '0.78rem', padding: '0.25rem 0.45rem' }}
                                value={it.shortRemarks} placeholder="e.g. claim no., GRN, what the customer said"
                                aria-label={`Remarks for the short-paid amount on ${it.referenceNo}`}
                                onChange={(e) => updateShortField(g.partyId, it.openItemId, { shortRemarks: e.target.value })} />
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Realized FX across the invoices (ADR-0031 receipt revision) — posted to FX Gain/Loss. */}
          {fxTotal !== 0 && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: fxTotal > 0 ? '#16a34a' : '#dc2626' }}>
              Exchange {fxTotal > 0 ? 'gain' : 'loss'} on this allocation: <strong>{homeCurrency} {money(Math.abs(fxTotal))}</strong>
              <span style={{ color: 'var(--text-muted)' }}> — invoices clear at their booked rates, the money at the receipt's rate ({receiptRate}); the difference posts to FX Gain / Loss.</span>
            </div>
          )}

          {/* ADR-0060: what stays open as short-paid — not money, so it is outside the balance. */}
          {shortSummary.count > 0 && (
            <div style={{ marginTop: '0.4rem' }}>
              <span className="badge" style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}
                title="Each short-paid amount stays open on the customer's ledger, with its reason, until it is received, set off against a credit note, or written off.">
                {homeCurrency} {money(shortSummary.base)} short-paid on {shortSummary.count} invoice{shortSummary.count > 1 ? 's' : ''} — kept open on the customer's ledger
              </span>
            </div>
          )}

          {/* Multi-customer is the exception — a quiet link appends another group. */}
          {!readOnly && groups.length > 0 && !!groups[0].partyId && (
            splitPickerOpen ? (
              <div style={{ maxWidth: 340, marginTop: '0.5rem' }}>
                <SyutSelect isAsync loadOptions={loadParties} defaultOptions cacheOptions={false} autoFocus
                  menuPortalTarget={document.body} value=""
                  onChange={(v) => {
                    const pid = (v as string) || '';
                    setSplitPickerOpen(false);
                    if (pid) addCustomer(pid);
                  }}
                  placeholder="Pick another customer" isClearable size="sm" />
              </div>
            ) : (
              <button onClick={() => setSplitPickerOpen(true)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '0.78rem', padding: '0.375rem 0', marginTop: '0.25rem', display: 'block' }}>
                + Split across another customer
              </button>
            )
          )}

          {/* Progressive disclosure — the Difference row. One click routes the
              current unapplied amount (sign-corrected so the line balances). */}
          {!readOnly && !loading && Math.abs(unapplied) > EPS && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Unapplied: <strong style={{ color: '#dc2626' }}>{money(unapplied)}</strong>
              </span>
              <button className="btn btn-secondary" style={smallBtn} onClick={() => addExtraLine('ON_ACCOUNT', unapplied)}>On Account</button>
              <button className="btn btn-secondary" style={smallBtn} onClick={() => addExtraLine('ADVANCE', unapplied)}>Advance</button>
              <button className="btn btn-secondary" style={smallBtn} onClick={() => addAdjustment('WHT_RECEIVABLE', -unapplied)}>WHT deducted</button>
              <button className="btn btn-secondary" style={smallBtn} onClick={() => addAdjustment('WRITE_OFF', -unapplied)}>Write off</button>
              <button className="btn btn-secondary" style={smallBtn} onClick={() => addAdjustment('ROUNDING', -unapplied)}>Rounding</button>
              {unapplied < -EPS && (
                <button className="btn btn-secondary" style={{ ...smallBtn, borderColor: 'var(--warning-text, #92400e)', color: 'var(--warning-text, #92400e)', background: 'var(--warning-bg, #fef3c7)' }}
                  title="The invoices are allocated in full but less money arrived: keep the shortfall open as short-paid on the newest allocated invoice (pick its reason after). Every invoice still closes."
                  onClick={shortPayExcess}>
                  Short-paid, keep open
                </button>
              )}
            </div>
          )}

          {/* Other lines — Advance / On Account / Adjustment (also renders
              hydrated draft lines even when unapplied is 0). */}
          {(extraLines.length > 0 || adjustments.length > 0) && (
            <>
              <div style={sectionTitle}>Other lines</div>
              {extraLines.map(l => (
                <div key={l.key} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                  <span className="badge" style={{ minWidth: 92, textAlign: 'center' }}>{l.mode === 'ADVANCE' ? 'Advance' : 'On Account'}</span>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <SyutSelect isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                      seedOptions={l.partyId ? [{ value: l.partyId, label: l.partyLabel }] : undefined}
                      menuPortalTarget={document.body} value={l.partyId} isDisabled={readOnly}
                      onChange={(v) => updateExtraLine(l.key, { partyId: (v as string) || '', partyLabel: (v && partyLabelCache.current.get(v as string)) || '' })}
                      placeholder="Customer" isClearable size="sm" />
                  </div>
                  {l.mode === 'ADVANCE' && (
                    <input type="text" className="form-input" value={l.referenceNo} disabled={readOnly}
                      onChange={(e) => updateExtraLine(l.key, { referenceNo: e.target.value })}
                      placeholder="Reference no" style={{ width: 150, ...cellInput }} />
                  )}
                  <input type="number" inputMode="decimal" className="form-input" value={l.amount} disabled={readOnly}
                    onChange={(e) => updateExtraLine(l.key, { amount: e.target.value })}
                    placeholder="Amount" style={{ width: 110, textAlign: 'right', ...cellInput }} />
                  <input type="text" className="form-input" value={l.remarks} disabled={readOnly}
                    onChange={(e) => updateExtraLine(l.key, { remarks: e.target.value })}
                    placeholder="Remarks" style={{ flex: 1, minWidth: 130, ...cellInput }} />
                  {!readOnly && (
                    <button title="Remove line" onClick={() => removeExtraLine(l.key)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.2rem' }}>
                      <IconSystem.x size={14} />
                    </button>
                  )}
                </div>
              ))}
              {adjustments.map(l => (
                <div key={l.key} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                  <span className="badge" style={{ minWidth: 92, textAlign: 'center' }}
                    title="Adjustment — positive amount = short-pay">
                    {ADJUSTMENT_TYPE_OPTIONS.find(o => o.value === l.purposeCode)?.label || l.purposeCode}
                  </span>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <SyutSelect isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                      seedOptions={l.partyId ? [{ value: l.partyId, label: l.partyLabel }] : undefined}
                      menuPortalTarget={document.body} value={l.partyId} isDisabled={readOnly}
                      onChange={(v) => updateAdjustment(l.key, { partyId: (v as string) || '', partyLabel: (v && partyLabelCache.current.get(v as string)) || '' })}
                      placeholder={l.purposeCode === 'ROUNDING' ? 'Customer (optional)' : 'Customer'} isClearable size="sm" />
                  </div>
                  <input type="number" inputMode="decimal" className="form-input" value={l.amount} disabled={readOnly}
                    onChange={(e) => updateAdjustment(l.key, { amount: e.target.value })}
                    placeholder="Amount" style={{ width: 110, textAlign: 'right', ...cellInput }} />
                  <input type="text" className="form-input" value={l.referenceNo} disabled={readOnly}
                    onChange={(e) => updateAdjustment(l.key, { referenceNo: e.target.value })}
                    placeholder={l.purposeCode === 'WHT_RECEIVABLE' ? 'WHT certificate no' : 'Reference'} style={{ width: 150, ...cellInput }} />
                  <input type="text" className="form-input" value={l.remarks} disabled={readOnly}
                    onChange={(e) => updateAdjustment(l.key, { remarks: e.target.value })}
                    placeholder="Remarks" style={{ flex: 1, minWidth: 130, ...cellInput }} />
                  {!readOnly && (
                    <button title="Remove adjustment" onClick={() => removeAdjustment(l.key)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.2rem' }}>
                      <IconSystem.x size={14} />
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {/* One-sentence footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)', paddingTop: '0.6rem' }}>
            <span>
              Allocated <strong>{money(customersTotal)}</strong> of <strong>{money(available)}</strong>
              {balanced
                ? <> · <strong style={{ color: '#16a34a' }}>Balanced ✓ — ready to post</strong></>
                : <> · Unapplied <strong style={{ color: '#dc2626' }}>{money(unapplied)}</strong></>}
            </span>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {isPending && canReturn && (
            <button className="btn btn-danger" onClick={() => setReturnOpen(true)} disabled={busy} style={{ marginRight: 'auto' }}>
              Return
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose} disabled={busy}>Close</button>
          {isPending && canSaveDraft && (
            <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={busy || loading}>
              Save Draft
            </button>
          )}
          {isPending && canPost && (
            <button className="btn btn-primary" onClick={handlePostClick} disabled={busy || loading || !balanced}>
              <IconSystem.check size={16} /> Post
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog open={postConfirmOpen} title="Post Allocation"
        message={`Post this allocation of ${money(customersTotal)} ${d.currencyCode || ''} for receipt ${d.receiptNo || ''}?${fxTotal !== 0 ? ` It includes an exchange ${fxTotal > 0 ? 'gain' : 'loss'} of ${homeCurrency} ${money(Math.abs(fxTotal))}.` : ''} The allocation voucher is posted now and the worksheet item becomes POSTED.`}
        confirmLabel="Post" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmPost} onCancel={() => setPostConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved allocation changes. Save Draft keeps them for later — discard anyway?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />

      <ConfirmDialog open={pendingCustomerChange !== null} title="Change Customer?"
        message={`You have typed allocations for ${groups[0]?.partyLabel || 'the current customer'}. Changing the customer discards those inputs. Continue?`}
        confirmLabel="Change Customer" cancelLabel="Keep" variant="danger"
        onConfirm={() => {
          const pid = pendingCustomerChange;
          setPendingCustomerChange(null);
          if (pid) applyPrimaryCustomer(pid);
        }}
        onCancel={() => setPendingCustomerChange(null)} />

      <ReturnReasonDialog isOpen={returnOpen} assignment={detail ?? assignment}
        onReturned={() => { setReturnOpen(false); onChanged(); onClose(); }}
        onClose={() => setReturnOpen(false)} />
    </>
  );
};

export default ReceiptAllocationModal;
